import { jsToFormData, paginatedTransform, rawTransform } from 'src/services/skuit/utils'
import { Csv, PaginatedResponse, Sku, SkuPathResponse, SkuThreshold } from '../../api/models'

import { API, CustomAxiosConfig } from './types'
import { ExportFilters } from '../skuit/types'
import fileDownload from 'js-file-download'

export interface ExportProps {
  fields: string[]
  exportFilters?: ExportFilters 
}

export interface Stats {
  // eslint-disable-next-line camelcase
  total_ordered: number
  // eslint-disable-next-line camelcase
  total_received: number
  // eslint-disable-next-line camelcase
  total_shipped: number
}

class ThresholdAPI {
  api: API

  constructor(api: API) {
    this.api = api
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<SkuThreshold>> => {
    const response = await this.api.get('/logistics/sku-thresholds', config)
    return paginatedTransform(SkuThreshold)(response.data)
  }

  create = async (data: any = {}, config: CustomAxiosConfig = {}): Promise<SkuThreshold> => {
    const response = await this.api.post('/logistics/sku-thresholds', data, config)
    return rawTransform(SkuThreshold)(response.data)
  }

  patch = async (guid: string, data: any, config: CustomAxiosConfig = {}): Promise<SkuThreshold> => {
    const response = await this.api.patch(`/logistics/sku-thresholds/${guid}`, data, config)
    return rawTransform(SkuThreshold)(response.data)
  }

  delete = async (guid: string, config: CustomAxiosConfig = {}): Promise<null> => {
    await this.api.delete(`/logistics/sku-thresholds/${guid}`, config)
    return null
  }
}

export class Skus {
  api: API

  constructor(api: API) {
    this.api = api
  }

  retrieve = async (guid: string, config: CustomAxiosConfig = {}): Promise<Sku> => {
    const response = await this.api.get(`/logistics/skus/${guid}`, config)
    return rawTransform(Sku)(response.data)
  }

  list = async (config: CustomAxiosConfig = {}): Promise<PaginatedResponse<Sku>> => {
    const response = await this.api.get('/logistics/skus', config)
    return paginatedTransform(Sku)(response.data)
  }

  storagePaths = async (
    config: CustomAxiosConfig = {}
  ): Promise<PaginatedResponse<SkuPathResponse>> => {
    const response = await this.api.get('/logistics/storage-paths/by-sku', config)
    return paginatedTransform(SkuPathResponse)(response.data)
  }

  reconcileFound = async (guid: string, config: CustomAxiosConfig = {}) => {
    const response = await this.api.post(`/logistics/skus/${guid}/reconcile-found`, config)
    return response.data
  }

  fixReservation = async (guid: string, config: CustomAxiosConfig = {}) => {
    const response = await this.api.post(`/logistics/skus/${guid}/fix-reservation`, config)
    return response.data
  }

  fix = async (guid: string, config: CustomAxiosConfig = {}) => {
    const response = await this.api.post(`/logistics/skus/${guid}/fix`, config)
    return response.data
  }

  uploadCsvSkus = async (config?: CustomAxiosConfig) => {
    const guid = config?.data.guid
    delete config?.data.guid
    const axiosConfig = {
      ...config,
      method: 'post' as 'post',
      url: `/logistics/skus/csv/clients/${guid}`,
      headers: { 'Content-Type': 'multipart/form-data' },
      data: jsToFormData(config?.data),
      customTransform: rawTransform(Csv),
    }
    return await this.api.axiosInstance(axiosConfig)
  }

  stats = async (guid: string, config: CustomAxiosConfig = {}): Promise<Stats> => {
    const response = await this.api.get(`/logistics/skus/${guid}/in-out-stats`, config)
    return response.data
  }

  csvAvailability = async (file: File, config?: CustomAxiosConfig) => {
    const form = new FormData()
    form.append('file', file)
    const axiosConfig = {
      ...config,
      method: 'post' as 'post',
      url: `/logistics/skus/availability/csv`,
      headers: { 'Content-Type': 'multipart/form-data' },
      data: form,
      customTransform: rawTransform(Csv),
    }
    return await this.api.axiosInstance(axiosConfig)
  }

  export = async ({ fields, exportFilters }: ExportProps) => {
    const guid: string | undefined = exportFilters?.guid
    const params = { fields: fields.join(','), products: guid }
    const response = await this.api.axiosInstance({
      url: '/logistics/skus/export',
      params,
      responseType: 'blob',
    })
    fileDownload(response.data, (guid ? `skus_product_${guid}.csv` : 'skus.csv')) 
  }

  get thresholds() {
    const api = new ThresholdAPI(this.api)
    Object.defineProperty(this, 'thresholds', { value: api })
    return api
  }
}
